/**
 * Local-first connect — the route is *resolved*, not offered (plan
 * `docs/plans/LOCAL-FIRST-CONNECT.md` §2/D2; the owner's words: *"the local
 * detection should happen automatically at each connection … before establish
 * try local"*).
 *
 * The rule, in the order it is applied:
 *
 *   1. a hub is **local** when the thing answering at its LAN address says it is
 *      the hub the account knows: the answer's `publicId` compared with the
 *      account's own `hubPublicId` — through
 *      `utils/adoptLink.ts` → `matchAdoptLinkToHub`, never restated here, and
 *      never taken from an mDNS TXT record (the hub's own answer is the fact);
 *   2. then it must **prove** it, because step 1 alone proves nothing
 *      (`services/hubChallenge.ts`, plan §2/D6): the app sends a nonce, the hub
 *      signs it with its identity key, and the signature is verified against the
 *      key the ACCOUNT pinned (`GET /api/hubs/:id/bindings` → `hubPublicKey`).
 *      A responder that copied a real hub's `publicId`/`publicKey` fails here,
 *      and the token never leaves;
 *   3. only then is the door the **account's own token**:
 *      `POST <lan>/api/auth/claim` with the app's `cloudAccessToken`, dialled at
 *      the rung that proved the key — **and that rung is the session's own
 *      address** (`LocalFirstSession.endpoint`), because a proof collected at one
 *      address must not license a token to another *nor point this window at
 *      another*: a responder that failed the proof is never this window's hub and
 *      never sees the bearer (card `t_2072e0e3`). The hub verifies that token
 *      against the cloud and matches its own user row by the **verified** email
 *      (F13, `otto/src/server/routes/auth.routes.ts`), then mints the session —
 *      which this module hands to `services/hubJoinFlow.ts` → `adoptLink`, the ONE
 *      place a hub session is written;
 *   4. every other ending is a **stated refusal** (`LocalRouteRefusal`), and the
 *      caller takes the tunnel: no cloud session, no LAN address, the account
 *      cannot name the hub, nothing answered, the responder is a different hub,
 *      it could not prove its key, or the hub refused the claim. A person picks
 *      a *hub*, not a transport.
 *
 * Nothing here opens a dialog, stores a preference or asks a question: the
 * surface's job is to call this and then say what it answered.
 *
 * Deliberately NOT here (other cards own them, and this module is written to be
 * the place they land):
 *
 *   - **the per-hub session store** and the retirement of device credentials for
 *     user devices (§2/D4);
 *   - **the offer dialog and its module** (§2/D2's "removed with this" list).
 */
import { matchAdoptLinkToHub } from '../utils/adoptLink';
import { ensureLocalNetworkAccess, type LocalNetworkAccessResult } from './localNetworkAccess';
import { lookupHubIdentity, type HubApiAttempt } from './hubIdentityLookup';
import type { HubFetch } from './hubFetch';
import { proveHubKey as defaultProveHubKey } from './hubChallenge';
// Type-only: these are the store-backed collaborators, and importing their
// modules would make this one unloadable outside Vite (`stores/auth.ts` reaches
// `import.meta.env` through the socket/WebRTC stores). The runtime gets them
// lazily, below; the tests inject their own.
import type { JoinHubInput, LinkedAccount, linkHubAccount } from './hubJoinFlow';
import type { AccountHubIdentity } from './hubAccountIdentity';
import { classifyJoinFailure, type JoinFailureCode } from './hubJoin';
import { claimFailureMessageKey } from './hubIdentityClaim';

/** What the claim can answer, as `hubJoinFlow` types it. */
type LinkResult = Awaited<ReturnType<typeof linkHubAccount>>;

/** The hub the caller is trying to open, as its card / tab knows it. */
export interface LocalFirstTarget {
  /** The LAN address to try. */
  ip: string;
  port: number;
  /** The scheme discovery measured for that address (`undefined` = nothing said). */
  secure?: boolean;
  /**
   * The hub's own id, when something already told us (`GET /api/hub/identity`,
   * mDNS) — what the session is pinned under (`otto_active_hub_key`) and what
   * the saved connection is keyed by.
   */
  hubId?: string | null;
  /**
   * The **account's** id for the hub (`GET /hubs` → `hubId`), under which the
   * cloud's `hubPublicId`/`hubPublicKey` are read. Falls back to `hubId` when it
   * is not given: for every hub the launcher lists the two are the same row.
   */
  accountHubId?: string | null;
  /** The hub's name, for the saved connection and the fallback sentence. */
  name?: string | null;
}

export interface LocalFirstOptions {
  /** The signed-in account's token (`authStore.cloudAccessToken`). */
  cloudAccessToken?: string | null;
  /** Injected by tests; the runtime reads the account's pinned id + key from the cloud. */
  accountIdentityFor?: (hubId: string) => Promise<AccountHubIdentity | null>;
  /** Injected by tests; the runtime claims through `hubJoinFlow`. */
  linkHubAccount?: (input: JoinHubInput) => Promise<LinkResult>;
  /** Injected by tests; the runtime asks for the proof over the hub's own transport. */
  proveHubKey?: typeof defaultProveHubKey;
  /** Injected by tests; the runtime's own hub transport (IPC bridge in Electron). */
  fetchImpl?: HubFetch;
  /** Injected by tests; a deterministic address ladder in place of the shared one. */
  attempts?: HubApiAttempt[];
  /**
   * Injected by tests; the browser's local-network-access gate. The runtime's own
   * is `services/localNetworkAccess.ts` — see the refusal it can answer with.
   */
  localNetworkAccess?: (target: { ip: string; port: number }) => Promise<LocalNetworkAccessResult>;
  /** Per-request budget. */
  timeoutMs?: number;
}

/**
 * The account's own identity for the hub — id and pinned key, read from the
 * cloud (`services/hubAccountIdentity.ts`) — loaded on first use, never at
 * import.
 */
async function defaultAccountIdentityFor(hubId: string): Promise<AccountHubIdentity | null> {
  const { accountIdentityFor } = await import('./hubAccountIdentity');
  return accountIdentityFor(hubId);
}

/** The claim itself, through the flow that owns the ladder and the vocabulary. */
async function defaultLinkHubAccount(input: JoinHubInput): Promise<LinkResult> {
  const { linkHubAccount } = await import('./hubJoinFlow');
  return linkHubAccount(input);
}

/**
 * Why the local route was not taken. Each value is a different fact, and the
 * connect surface words each one differently — that is the whole point of
 * answering with a value instead of a boolean.
 */
export type LocalRouteRefusal =
  /** No cloud account is signed in: there is no token to claim with. */
  | 'no-cloud-session'
  /** The caller has no address to dial (a hub that announced none). */
  | 'no-lan-address'
  /** The account has no `hubPublicId`/`hubPublicKey` for this hub — not one of its hubs. */
  | 'no-account-identity'
  /** Nothing answered at the LAN address, or it does not name itself. */
  | 'unreachable'
  /** Something answered, and it is not this hub (a different device, or a copy). */
  | 'not-this-hub'
  /** Something answered as this hub and could not prove it holds the pinned key (§2/D6). */
  | 'not-proven'
  /** Nothing there could be asked for a proof — no challenge route, a refusal, no answer. */
  | 'cannot-prove'
  /** The hub refused the claim (`no_matching_user`, `user_disabled`, …). */
  | 'refused'
  /**
   * **The browser is holding the request** for its `local-network-access`
   * permission (card `t_4623769a`): a public origin dialling a private address
   * whose permission the person has not allowed gets no traffic, no error and no
   * rejection — measured `HELD 20000 ms` — so the LAN attempt is bounded and
   * abandoned with this name, and the surface says what the browser is waiting
   * for (`services/localNetworkAccess.ts`).
   */
  | 'permission-held';

/**
 * May the tunnel take the session when the local route ended this way? (§2/D2
 * step 3 — "only when there is no LAN answer, or the claim refuses, fall back to
 * the tunnel — and say so once".)
 *
 * `services/hubConnect.ts` is the caller, and this lives here so the answer is a
 * unit test rather than a condition buried in a store-backed module: a hub the
 * account cannot name (`no-account-identity`) has no tunnel either — it is not
 * one of its hubs — and neither has a device with no cloud session
 * (`no-cloud-session`) or a caller with no address to try (`no-lan-address`).
 */
export function localRefusalFallsBackToTunnel(reason: LocalRouteRefusal): boolean {
  return (
    reason === 'unreachable' ||
    reason === 'not-this-hub' ||
    reason === 'not-proven' ||
    reason === 'cannot-prove' ||
    reason === 'refused' ||
    // The browser holding the request is exactly the case where the tunnel is the
    // second honest answer ("allow it, or continue over the cloud"): the hub may
    // be reachable and this app simply cannot say until the permission is given.
    reason === 'permission-held'
  );
}

/** The LAN route, resolved and opened. */
export interface LocalFirstSession {
  ok: true;
  route: 'local';
  /**
   * The **address this session belongs to** — the rung that proved the key, and
   * therefore the address every later hub request goes to.
   *
   * It is not always the address the caller named: the ladder ends at the hub's
   * own raw API port (`hubApiAttempts`), so a caller dialling a stand-in on the
   * same host is answered by the real hub on another port. Pinning the caller's
   * address instead would point this window — and the bearer the claim just
   * minted — at the responder that FAILED the proof (card `t_2072e0e3`, measured
   * by `t_fa5bdc20`: the session was pinned to the impostor's port and six
   * `GET /api/users/me` calls carried `Authorization: Bearer …` to it).
   */
  endpoint: LocalRouteEndpoint;
  /** The rung that answered the claim (`http(s)://<host>:<port>/api`) — the same address as `endpoint`. */
  baseUrl: string;
  /** The `publicId` the responder named — equal to the account's own. */
  publicId: string;
  /** The hub's own id, for pinning the session and the saved connection. */
  hubId: string | null;
  /** Its name, as it named itself (then as the caller did). */
  name: string | null;
  /** The session the claim minted — already adopted by `openLocalFirstSession`. */
  account: LinkedAccount;
}

/**
 * An address that can hold a hub session, as the ladder spells one: the host the
 * caller named, and the port **and scheme of the rung that answered**.
 *
 * Both fields are measured facts, not hints — the port is the rung's own (a hub
 * behind TLS on `:443` and a legacy raw API on `:16161` are one hub with two
 * doors, and only one of them proved the key), and the scheme is the one the
 * proof travelled over.
 */
export interface LocalRouteEndpoint {
  ip: string;
  port: number;
  secure: boolean;
}

/** The LAN route was not taken; the tunnel is what is left. */
export interface LocalFirstFallback {
  ok: false;
  route: 'cloud';
  reason: LocalRouteRefusal;
  /** The server's own code, when a server refused (`no_matching_user`, …). */
  serverCode: string | null;
  /** The HTTP status of the refusal, when there was one. */
  status: number | null;
  /** The sentence the surface may paint, as an i18n key (`auth.claim.*`). */
  messageKey: string | null;
  /** The server's own message, for the log line. */
  message: string | null;
}

export type LocalFirstRoute = LocalFirstSession | LocalFirstFallback;

function fallback(
  reason: LocalRouteRefusal,
  extra: {
    serverCode?: string | null;
    status?: number | null;
    messageKey?: string | null;
    message?: string | null;
  } = {},
): LocalFirstFallback {
  return {
    ok: false,
    route: 'cloud',
    reason,
    serverCode: extra.serverCode ?? null,
    status: extra.status ?? null,
    messageKey: extra.messageKey ?? null,
    message: extra.message ?? null,
  };
}

/**
 * The hub's own refusal → the sentence a connect surface can paint.
 *
 * The three codes `auth.claim.*` words are the hub's own (`no_matching_user`,
 * `user_disabled`, `invalid_cloud_token`); everything else — a rate limit, a
 * cloud that cannot be reached, a body this client cannot read — is
 * `auth.claim.failed`, which asks the person to get help rather than inventing a
 * cause. The copy lives in the six catalogues, never here.
 */
export function localClaimMessageKey(code: JoinFailureCode): string {
  if (code === 'no_matching_user' || code === 'user_disabled' || code === 'invalid_cloud_token') {
    return claimFailureMessageKey(code);
  }
  return claimFailureMessageKey('unknown');
}

/**
 * Resolve the route for one hub and, when it is local, spend the claim.
 *
 * Order matters and is the plan's: the account's own identity for the hub is
 * read first (nothing is dialled for a hub the account cannot name), then the
 * hub is asked who it is at its LAN address (`GET /api/hub/identity`, the same
 * answer discovery reads and the same session cache), the two ids are compared
 * with the adoption link's rule, the responder must then **prove** it holds the
 * account's pinned key (a signed nonce, §2/D6), and only then does the token
 * leave the app — at the rung that proved it, which is the address the returned
 * session carries (`LocalFirstSession.endpoint`, card `t_2072e0e3`).
 *
 * The claim is one cloud round trip per hub per *session*: the caller reaches
 * this function only when this window holds no live session for the hub
 * (`services/hubConnect.ts` → `connectLocalSilently`), and the session it mints
 * is what makes every later connect local without one.
 */
export async function resolveLocalRoute(
  target: LocalFirstTarget,
  options: LocalFirstOptions = {},
): Promise<LocalFirstRoute> {
  const token = (options.cloudAccessToken ?? '').trim();
  if (!token) return fallback('no-cloud-session');

  const host = (target.ip ?? '').trim();
  const port = Number(target.port);
  if (!host || !Number.isFinite(port) || port <= 0) return fallback('no-lan-address');

  // 1. What the ACCOUNT knows about this hub — its id AND the key it pinned.
  //    Both spellings the app may hold are asked, but the caller's own first: the
  //    `hubId` a connect was built with names the hub the caller is thinking about,
  //    while `accountHubId` is the spelling that came beside it (and, for a window
  //    with no record of its own, the shared `otto_active_hub_key` — another tab's
  //    last write, card `t_f000e669`). Asking the foreign one first resolves
  //    ANOTHER hub's identity, the `not-this-hub` probe below then refuses the hub
  //    that is answering at this address, and the connect falls back to the tunnel
  //    with the wrong id.
  const lookup = options.accountIdentityFor ?? defaultAccountIdentityFor;
  const ids = [
    ...new Set(
      [target.hubId, target.accountHubId].map((value) => (value ?? '').trim()).filter((value) => value.length > 0),
    ),
  ];
  let accountIdentity: AccountHubIdentity | null = null;
  for (const id of ids) {
    accountIdentity = await lookup(id);
    if (accountIdentity) break;
  }
  // No pinned key means no way to tell the hub from a copy of it — which is the
  // same answer as not knowing the hub at all: the local route is not taken.
  if (!accountIdentity) return fallback('no-account-identity');

  // 1b. **May this app reach a private address at all from this page?** The
  //     browser's `local-network-access` gate (card `t_4623769a`). From a public
  //     origin, a call to a private hub address is HELD while the permission is
  //     unanswered — no traffic, no error, no rejection (measured `HELD 20000 ms`)
  //     — so the first LAN request of a connect is this bounded probe, and it is
  //     issued from here so it still carries the click's activation (the request
  //     that raises Chrome's bubble needs it). Where the gate does not apply
  //     (Electron, Capacitor, a page the hub itself serves, an already-granted
  //     permission) nothing is dialled and nothing changes.
  const access = await (options.localNetworkAccess ?? ensureLocalNetworkAccess)({ ip: host, port });
  if (access.verdict === 'permission-held') {
    console.info('[LocalFirst] the browser is holding the LAN request — permission not allowed', {
      hub: `${host}:${port}`,
      permission: access.state,
      waitedMs: access.waitedMs,
      activated: access.activated,
    });
    return fallback('permission-held');
  }

  // 2. What the hub says at the LAN address — read through discovery's own
  //    cache, so a hub this session already asked is not asked again.
  const answer = await lookupHubIdentity(
    { ip: host, port },
    {
      fetchImpl: options.fetchImpl as typeof fetch | undefined,
      attempts: options.attempts,
      timeoutMs: options.timeoutMs,
    },
  );
  if (answer.state !== 'known' || !answer.identity) return fallback('unreachable');

  if (matchAdoptLinkToHub({ hubPublicId: accountIdentity.publicId }, answer.identity.publicId) !== 'ok') {
    return fallback('not-this-hub');
  }

  // 3. The proof (§2/D6). The identity above is plaintext and copyable; the
  //    token is not. A responder that cannot sign a nonce this app chose with
  //    the key the account pinned is not the hub, and it never sees the token.
  const prove = options.proveHubKey ?? defaultProveHubKey;
  const proof = await prove({
    ip: host,
    port,
    publicId: accountIdentity.publicId,
    publicKey: accountIdentity.publicKey,
    fetchImpl: options.fetchImpl,
    attempts: options.attempts,
    timeoutMs: options.timeoutMs,
  });
  if (!proof.ok) {
    return fallback(proof.reason, { message: proof.detail });
  }

  // 4. The address this session belongs to — the rung that PROVED the key, not
  //    the one the caller named. The claim is dialled at that rung and nowhere
  //    else, and the same address is what `LocalFirstSession.endpoint` carries
  //    out of here: a proof collected at one address must never license a token
  //    to another, and the session it mints must not be pinned at another
  //    either (card `t_2072e0e3`).
  const endpoint: LocalRouteEndpoint = {
    ip: host,
    port: proof.attempt.port,
    secure: proof.attempt.scheme === 'https',
  };
  const link = await (options.linkHubAccount ?? defaultLinkHubAccount)({
    host,
    port: endpoint.port,
    secure: endpoint.secure,
    hubId: answer.identity.hubId || target.hubId || null,
    name: answer.identity.name || target.name || null,
    publicId: answer.identity.publicId,
    cloudAccessToken: token,
    fetchImpl: options.fetchImpl,
    attempts: [proof.attempt],
    timeoutMs: options.timeoutMs,
  });

  if (!link.ok) {
    const refused = classifyJoinFailure(link.status, { code: link.serverCode, message: link.message });
    return fallback('refused', {
      serverCode: link.serverCode ?? refused.serverCode,
      status: link.status,
      messageKey: localClaimMessageKey(link.code),
      message: link.message,
    });
  }

  return {
    ok: true,
    route: 'local',
    endpoint,
    baseUrl: link.baseUrl,
    publicId: answer.identity.publicId,
    hubId: link.identity.hubId || target.hubId || null,
    name: link.identity.name || target.name || null,
    account: link.account,
  };
}

/**
 * Open the session the claim minted: pin this window at the address the session
 * belongs to — **the rung that proved the key** (`route.endpoint`, card
 * `t_2072e0e3`) — take the hub session (`adoptLink` — the one writer), then
 * remember the hub as a connection so the card keeps offering it.
 *
 * The address comes off the ROUTE and never off the caller: the caller names the
 * address it meant to dial, and the ladder may have proved the key on another
 * rung of the same host (`hubApiAttempts`). Pinning the caller's address would
 * make this window — and the bearer the claim just minted — talk to the
 * responder that failed the proof, which is the defect this signature cannot
 * express any more: there is no second argument to get it from.
 *
 * The order is `adoptLink`'s and is not incidental: the window is pinned BEFORE
 * the session is taken, because the store files its per-hub token under the hub
 * this window is on (`stores/auth.ts` → `tabHubTokenKey`). Nothing is written
 * before the claim answered.
 */
export async function openLocalFirstSession(route: LocalFirstSession): Promise<void> {
  const { endpoint } = route;
  // Loaded here, not at import: `hubJoinFlow` reaches the stores (see above).
  const { adoptLink } = await import('./hubJoinFlow');
  await adoptLink(
    { account: route.account },
    { host: endpoint.ip, port: endpoint.port, secure: endpoint.secure, hubId: route.hubId, name: route.name },
  );

  try {
    const { saveConnection } = await import('./savedConnections');
    await saveConnection({
      id: `${endpoint.ip}:${endpoint.port}`,
      name: route.name || route.hubId || `${endpoint.ip}:${endpoint.port}`,
      ip: endpoint.ip,
      port: endpoint.port,
      // The transport this hub answered the proof on (card `t_cf6ea0e5`): a hub
      // reached over TLS must not be remembered as cleartext — and the measured
      // scheme is stronger than the caller's hint.
      secure: endpoint.secure,
      isCloud: false,
      hubId: route.hubId || undefined,
    });
  } catch (error) {
    // A hub we just opened is usable this session; the card is the convenience.
    console.warn('[LocalFirst] could not save the connection:', error);
  }
}

/** Re-exported so a caller can type a target without reaching into two modules. */
export type { LinkedAccount };
